import { first, truncate } from 'lodash';
import { ReactNode } from 'react';
import { type IntlShape } from 'react-intl';
import { CalibrationInfo, CalibrationOriginalItem, Question } from 'types';
import { IMPORTANT_ANSWER } from 'views/Widgets/Dashboards/TeamDashboard/Calibration/utils';
import { YES_NO_OPTIONS } from 'views/Widgets/Inputs/MultipleChoiceInput';

type FormatterOptions = {
  maxLength?: number;
  numeric?: boolean;
};

export const formatQuestion = (
  question: Question,
  answer: unknown,
  formatMessage: IntlShape['formatMessage'],
  options: FormatterOptions = {
    maxLength: undefined,
    numeric: false,
  }
): string => {
  if (nonSignificant(answer)) {
    return '';
  }
  const formattedAnswer = formatForType(
    question,
    answer,
    formatMessage,
    options
  );
  if (options.maxLength) {
    return truncate(formattedAnswer, {
      length: options.maxLength,
      omission: '...',
    });
  }
  return formattedAnswer;
};

const nonSignificant = (answer: unknown): boolean => {
  return (
    answer === null ||
    answer === undefined ||
    (typeof answer === 'string' && answer.trim() === '') ||
    (Array.isArray(answer) && answer.length === 0)
  );
};

const formatForType = (
  question: Question,
  answer: unknown,
  formatMessage: IntlShape['formatMessage'],
  options: FormatterOptions
): string => {
  switch (question.type) {
    case 'MULTIPLE_CHOICE':
      return question.options.find((opt) => opt.id === answer)?.name ?? '';
    case 'YES_NO':
      return (
        YES_NO_OPTIONS(formatMessage).find((opt) => opt.id === String(answer))
          ?.name ?? ''
      );
    case 'LIKERT': {
      const value = question.scale.find((opt) => opt.value === answer);
      if (!value) {
        return '';
      }
      if (options.numeric) {
        return `${value.value}`;
      }
      return `${value.name} (${value.value})`;
    }
    case 'DROPDOWN':
      return question.objects.find((opt) => opt.id === answer)?.name ?? '';
    default:
      throw new Error(`Unsupported question type: ${question?.type}`);
  }
};

export const formatChangeDelta = (
  question: Question,
  calibrationInfo: CalibrationInfo
): ReactNode => {
  const lastCalibrationItem = first(calibrationInfo.calibrationItems)?.value;
  const priorityOriginalValue = calibrationInfo.originalResponses.reduce(
    (result: unknown, item: CalibrationOriginalItem) => {
      if (item.priority === IMPORTANT_ANSWER || !result) {
        return item.value;
      }
      return result;
    },
    undefined
  );

  if (priorityOriginalValue == null || lastCalibrationItem == null) {
    return undefined;
  }

  const delta = calculateDelta(
    question,
    priorityOriginalValue,
    lastCalibrationItem
  );
  if (delta == null) {
    return <i className="fe fe-shuffle"></i>;
  }
  switch (question.type) {
    case 'YES_NO':
    case 'DROPDOWN':
      return (
        <span>
          {`(`}
          {directionIndicator(delta)}
          {`)`}
        </span>
      );
    default:
      break;
  }

  return <span>{`(${sign(delta)}${delta})`}</span>;
};

const directionIndicator = (delta: number): ReactNode => {
  if (delta > 0) {
    return <i className="fe fe-arrow-up"></i>;
  } else if (delta < 0) {
    return <i className="fe fe-arrow-down"></i>;
  }
  return '=';
};

const calculateDelta = (
  question: Question,
  originalValue: unknown,
  calibratedValue: unknown
): number | undefined => {
  if (!originalValue || !calibratedValue) {
    return undefined;
  }
  switch (question.type) {
    case 'YES_NO':
      return originalValue === calibratedValue
        ? 0
        : originalValue === 'true'
          ? -1
          : 1;
    case 'LIKERT': {
      const value = (calibratedValue as number) - (originalValue as number);
      return value;
    }
    case 'DROPDOWN':
    case 'MULTIPLE_CHOICE':
      return originalValue === calibratedValue ? 0 : undefined;
    default:
      throw new Error(`Unsupported question type: ${question?.type}`);
  }
};

const sign = (num: number): string => {
  if (num > 0) {
    return '+';
  } else if (num === 0) {
    return '±';
  }
  return '';
};
